/** @format */

import {
  Directive,
  EmbeddedViewRef,
  ElementRef,
  Renderer2,
  RendererStyleFlags2,
  TemplateRef,
  ViewContainerRef,
  inject,
  input,
} from '@angular/core';

@Directive({
  selector: 'img[sharedUtilsDirectiveImgErrorFallback]',
  host: {
    '(error)': 'handleError()',
    '(load)': 'handleLoad()',
  },
})
export class ImgErrorFallbackDirective {
  public readonly imgErrorFallback = input.required<TemplateRef<unknown>>();

  private readonly elementRef = inject<ElementRef<HTMLImageElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private hasHandledError = false;
  private fallbackViewRef: EmbeddedViewRef<unknown> | null = null;
  private originalAriaHiddenValue: string | null = null;

  public handleError(): void {
    if (this.hasHandledError) {
      return;
    }

    const imageElement = this.elementRef.nativeElement;
    const parentElement = imageElement.parentElement;

    this.markImageAsErrored(imageElement);

    if (!parentElement) {
      return;
    }

    this.renderFallback(parentElement, imageElement.nextSibling, this.imgErrorFallback());
  }

  public handleLoad(): void {
    if (!this.hasHandledError) {
      return;
    }

    const imageElement = this.elementRef.nativeElement;

    this.restoreImageFromError(imageElement);
    this.removeFallback();
  }

  // Track original aria-hidden state and hide broken image from layout and assistive tech.
  private markImageAsErrored(imageElement: HTMLImageElement): void {
    this.hasHandledError = true;
    this.originalAriaHiddenValue = imageElement.getAttribute('aria-hidden');

    this.renderer.setAttribute(imageElement, 'aria-hidden', 'true');
    this.renderer.setStyle(imageElement, 'display', 'none', RendererStyleFlags2.Important);
  }

  // Render projected fallback template as siblings after the image.
  private renderFallback(
    parentElement: HTMLElement,
    nextSibling: ChildNode | null,
    fallbackTemplate: TemplateRef<unknown>,
  ): void {
    const fallbackViewRef = this.viewContainerRef.createEmbeddedView(fallbackTemplate);
    fallbackViewRef.detectChanges();
    this.fallbackViewRef = fallbackViewRef;

    for (const rootNode of fallbackViewRef.rootNodes) {
      this.renderer.insertBefore(parentElement, rootNode, nextSibling);
    }
  }

  // Restore aria-hidden state to what existed before the image error.
  private restoreImageFromError(imageElement: HTMLImageElement): void {
    if (this.originalAriaHiddenValue === null) {
      this.renderer.removeAttribute(imageElement, 'aria-hidden');
    } else {
      this.renderer.setAttribute(imageElement, 'aria-hidden', this.originalAriaHiddenValue);
    }

    this.renderer.removeStyle(imageElement, 'display', RendererStyleFlags2.Important);
    this.originalAriaHiddenValue = null;
  }

  // Remove fallback element and reset error state once image recovers.
  private removeFallback(): void {
    if (this.fallbackViewRef) {
      this.fallbackViewRef.destroy();
      this.fallbackViewRef = null;
    }

    this.hasHandledError = false;
  }
}
